import { get } from "@src/appV2/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import z from "zod";

export const PayoutCapabilitySchema = z.enum(["instant", "standard", "none"]);
export type PayoutCapability = z.infer<typeof PayoutCapabilitySchema>;

export const PayoutScheduleSchema = z.enum(["instant", "standard"]);
export type PayoutSchedule = z.infer<typeof PayoutScheduleSchema>;

export const PayoutSettingsAttributesSchema = z.object({
  payoutSchedule: PayoutScheduleSchema,
  preferredExternalAccountStripeId: z.string().optional(),
});

export const GetPayoutSettingsResponseSchema = z.object({
  data: z.object({
    attributes: PayoutSettingsAttributesSchema,
    meta: z.object({
      payoutCapability: PayoutCapabilitySchema,
      instantPayoutFee: z.number(),
    }),
  }),
});

type GetPayoutSettingsResponse = z.infer<typeof GetPayoutSettingsResponseSchema>;

export interface GetWorkerPayoutSettingsErrorResponseBody {
  message: string;
}

export function useGetWorkerPayoutSettings(
  workerId: string
): UseQueryResult<GetPayoutSettingsResponse, GetWorkerPayoutSettingsErrorResponseBody> {
  const queryUrl = `${environmentConfig.REACT_APP_PAYMENTS_API_URL}/accounts/${workerId}/payout-settings`;
  return useQuery({
    queryKey: [queryUrl],
    queryFn: async () => {
      const response = await get({
        url: queryUrl,
        responseSchema: GetPayoutSettingsResponseSchema,
      });
      return response.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_PAYOUT_SETTINGS_FAILURE },
  });
}
